import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { HomeRoute } from './home-route';
import { SignupRoute } from './auth/signup-route';
import { SigninRoute } from './auth/signin-route';
import { CallbackRoute } from './auth/callback-route';
import { CreateStoreRoute } from './create-store-route';
import { DashboardRoute } from './dashboard-route';
import { ProductsListRoute } from './products/products-list-route';
import { ProductNewRoute } from './products/product-new-route';
import { ProductDetailRoute } from './products/product-detail-route';
import { ProductEditRoute } from './products/product-edit-route';
import { CategoriesRoute } from './categories/categories-route';
import { PromotionsRoute } from './promotions/promotions-route';
import { PromotionalAdNewRoute } from './promotions/promotional-ad-new-route';
import { PromotionalAdEditRoute } from './promotions/promotional-ad-edit-route';
import { OfferCampaignsRoute } from './promotions/offer-campaigns-route';
import { OfferCampaignNewRoute } from './promotions/offer-campaign-new-route';
import { OfferCampaignEditRoute } from './promotions/offer-campaign-edit-route';
import { PromotionKitRoute } from './promotions/promotion-kit-route';
import { BillingRoute } from './billing/billing-route';
import { OrdersListRoute } from './orders/orders-list-route';
import { OrderPrepareRoute } from './orders/order-prepare-route';
import { OrderDetailRoute } from './orders/order-detail-route';
import { OrderShippingLabelRoute } from './orders/order-shipping-label-route';
import { ReviewsRoute } from './reviews/reviews-route';
import { CouriersRoute } from './couriers/couriers-route';
import { SubscribersRoute } from './subscribers/subscribers-route';
import { StockRoute } from './stock/stock-route';
import { BarcodeIntakeRoute } from './stock/barcode-intake-route';
import { ReceivingListRoute } from './stock/receiving/receiving-list-route';
import { ReceivingNewRoute } from './stock/receiving/receiving-new-route';
import { ReceivingDraftRoute } from './stock/receiving/receiving-draft-route';
import { ReceivingReviewRoute } from './stock/receiving/receiving-review-route';
import { StockReceiptRoute } from './stock/receiving/stock-receipt-route';
import { LegacyStorefrontRedirect } from './storefront/legacy-storefront-redirect';
import { StorefrontEntryRoute } from './storefront/storefront-entry-route';
import { StorefrontTemplateEditRoute } from './storefront/storefront-template-edit-route';
import { StorefrontTemplatesRoute } from './storefront/storefront-templates-route';
import { StoreSettingsRoute } from '@/features/store-settings';
import { AuthGuard } from '@/components/auth/auth-guard';
import { AdminShellLayout } from '@/components/layout/admin-shell-layout';

// Data-router migration (F-027 D-2): we switched from <BrowserRouter>
// + JSX <Routes> to createBrowserRouter + <RouterProvider> so
// useBlocker from react-router-dom v6.26 is available. useBlocker is
// the only clean way to intercept sidebar clicks, browser back/forward,
// AND programmatic navigate() — AC15/AC16 require all three. The old
// JSX tree is semantically equivalent and each entry maps 1:1 to a
// route object here. See 02-design.md §1 for the before/after.
const router = createBrowserRouter([
  // PUBLIC routes — HomeRoute, SigninRoute, SignupRoute each wrap
  // their body in <RedirectIfAuthed/>, so active merchants bounce
  // to /dashboard and no-membership sessions see access state via render-phase
  // <Navigate replace/>. No flicker. See bugfix
  // B-2026-04-14-admin-ux-nav-and-authed-redirect.
  { path: '/', element: <HomeRoute /> },
  { path: '/auth/signup', element: <SignupRoute /> },
  { path: '/auth/signin', element: <SigninRoute /> },
  { path: '/auth/callback', element: <CallbackRoute /> },

  // /create-store is intentionally OUTSIDE AdminShellLayout: the
  // F-AUTH-REBUILD keeps /create-store outside the shell and blocks
  // no-membership sessions with access state until a future approved
  // onboarding flow replaces it.
  // See B-2026-04-14 item 5.
  {
    path: '/create-store',
    element: (
      <AuthGuard mode="require-no-store">
        <CreateStoreRoute />
      </AuthGuard>
    ),
  },

  // AUTHED routes with persistent shell chrome. AdminShellLayout
  // gates on session + store membership before rendering any chrome,
  // so child routes no longer need individual AuthGuard wrappers.
  // Closes ADMIN-TBD-shell-unauth-flash. See B-2026-04-14 item 4.
  {
    element: <AdminShellLayout />,
    children: [
      { path: '/dashboard', element: <DashboardRoute /> },
      { path: '/dashboard/settings', element: <StoreSettingsRoute /> },
      { path: '/products', element: <ProductsListRoute /> },
      { path: '/categories', element: <CategoriesRoute /> },
      { path: '/promotions', element: <PromotionsRoute /> },
      { path: '/promotions/new', element: <PromotionalAdNewRoute /> },
      { path: '/promotions/:adId/edit', element: <PromotionalAdEditRoute /> },
      { path: '/promotions/campaigns', element: <OfferCampaignsRoute /> },
      { path: '/promotions/campaigns/new', element: <OfferCampaignNewRoute /> },
      { path: '/promotions/campaigns/:campaignId/edit', element: <OfferCampaignEditRoute /> },
      { path: '/promotions/kit', element: <PromotionKitRoute /> },
      { path: '/billing', element: <BillingRoute /> },
      { path: '/orders', element: <OrdersListRoute /> },
      { path: '/orders/prepare', element: <OrderPrepareRoute /> },
      { path: '/orders/:orderId', element: <OrderDetailRoute /> },
      { path: '/orders/:orderId/label', element: <OrderShippingLabelRoute /> },
      { path: '/reviews', element: <ReviewsRoute /> },
      { path: '/couriers', element: <CouriersRoute /> },
      { path: '/subscribers', element: <SubscribersRoute /> },
      { path: '/stock', element: <StockRoute /> },
      { path: '/stock/barcode', element: <BarcodeIntakeRoute /> },
      // F-148 reviewed stock receiving (design §10.1). Entry is a gated button on /stock;
      // there is intentionally no sidebar item.
      { path: '/stock/receiving', element: <ReceivingListRoute /> },
      { path: '/stock/receiving/new', element: <ReceivingNewRoute /> },
      { path: '/stock/receiving/drafts/:draftId', element: <ReceivingDraftRoute /> },
      { path: '/stock/receiving/drafts/:draftId/review', element: <ReceivingReviewRoute /> },
      { path: '/stock/receiving/receipts/:receiptId', element: <StockReceiptRoute /> },
      // Fixed website templates (PM PRD): `/storefront` opens the storefront of a
      // merchant who has one and the template library for one who has not. The
      // migration continuity gate passed; old bookmarks now enter the fixed editor.
      { path: '/storefront', element: <StorefrontEntryRoute /> },
      { path: '/storefront/templates', element: <StorefrontTemplatesRoute /> },
      // Epic #228 P3: each template keeps its own draft, edited at its own address.
      {
        path: '/storefront/templates/:templateId/edit',
        element: <StorefrontTemplateEditRoute />,
      },
      { path: '/storefront/home', element: <LegacyStorefrontRedirect /> },
      { path: '/storefront/home/editor', element: <LegacyStorefrontRedirect /> },
      { path: '/products/new', element: <ProductNewRoute /> },
      { path: '/products/:productId', element: <ProductDetailRoute /> },
      { path: '/products/:productId/edit', element: <ProductEditRoute /> },
    ],
  },
]);

export function AppRouter() {
  return <RouterProvider router={router} future={{ v7_startTransition: true }} />;
}
